import type { SerializedNode, ConversionContext, RGBA, Gradient } from '../types';
import { getAttribute } from '../parser';
import {
  parseColor,
  parsePadding,
  parseBorderRadius,
  parseInteger,
  parseFloat_,
  parsePosition,
  parsePositionOffset,
  inferPositionAnchorsState,
  parseShadowAttributes,
  SHADOW_ATTRIBUTES,
} from '../utils';
import { ICON_DEFAULTS, getLeafFillSpace } from '../utils/defaults';
import { ICON_NODE_NAME } from '../../utils/constants';
import { checkIsGradient } from '../../utils/styleHelpers';

/**
 * Transform an <icon> XML element into a CraftJS Icon node
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the Icon
 */
export const transformIcon = (element: Element, context: ConversionContext): SerializedNode => {
  // Parse icon name
  const iconNameAttr = getAttribute(element, 'iconName');
  const iconName = iconNameAttr || ICON_DEFAULTS.iconName;

  // Parse icon style (solid or regular)
  const iconStyleAttr = getAttribute(element, 'iconStyle');
  const iconStyle = mapIconStyle(iconStyleAttr) || ICON_DEFAULTS.iconStyle;

  // Parse color
  const colorAttr = getAttribute(element, 'color');
  const color = colorAttr ? parseColor(colorAttr) : ICON_DEFAULTS.color;

  // Parse opacity
  const opacityAttr = getAttribute(element, 'opacity');
  const opacity = parseFloat_(opacityAttr, ICON_DEFAULTS.opacity);

  // Parse dimensions
  const widthAttr = getAttribute(element, 'width');
  const width = widthAttr || ICON_DEFAULTS.width;

  const heightAttr = getAttribute(element, 'height');
  const height = heightAttr || ICON_DEFAULTS.height;

  // Parse spacing
  const paddingAttr = getAttribute(element, 'padding');
  const padding = parsePadding(paddingAttr, ICON_DEFAULTS.padding);

  const marginAttr = getAttribute(element, 'margin');
  const margin = parsePadding(marginAttr, ICON_DEFAULTS.margin);

  // Parse border
  const borderRadiusAttr = getAttribute(element, 'borderRadius');
  const borderRadius = parseBorderRadius(borderRadiusAttr, ICON_DEFAULTS.borderRadius);

  const borderWidthAttr = getAttribute(element, 'borderWidth');
  const borderWidth = parseInteger(borderWidthAttr, ICON_DEFAULTS.borderWidth);

  const borderColorAttr = getAttribute(element, 'borderColor');
  const borderColor = borderColorAttr ? parseColor(borderColorAttr) : undefined;

  // Parse background color
  const bgAttr = getAttribute(element, 'backgroundColor');
  const backgroundColor = bgAttr ? parseColor(bgAttr) : undefined;

  // Parse position attributes
  const positionAttr = getAttribute(element, 'position');
  const position = parsePosition(positionAttr) || ICON_DEFAULTS.position;

  const topAttr = getAttribute(element, 'top');
  const top = parsePositionOffset(topAttr);

  const leftAttr = getAttribute(element, 'left');
  const left = parsePositionOffset(leftAttr);

  const rightAttr = getAttribute(element, 'right');
  const right = parsePositionOffset(rightAttr);

  const bottomAttr = getAttribute(element, 'bottom');
  const bottom = parsePositionOffset(bottomAttr);

  const zIndexAttr = getAttribute(element, 'zIndex');
  const zIndex = parseInteger(zIndexAttr, ICON_DEFAULTS.zIndex);

  // Infer position anchors state from provided values
  const positionAnchorsState = inferPositionAnchorsState(position, top, left, right, bottom);

  // Parse shadow attributes
  const shadow = parseShadowAttributes(element);

  // Parse breakpoint overrides
  const tablet = parseBreakpointOverrides(element, 'tablet');
  const mobile = parseBreakpointOverrides(element, 'mobile');

  // Build props object
  const props: Record<string, unknown> = {
    iconName,
    iconStyle,
    color,
    opacity,
    width,
    height,
    padding,
    margin,
    borderRadius,
    borderWidth,
    aspectRatio: ICON_DEFAULTS.aspectRatio,
    aspectRatioLock: ICON_DEFAULTS.aspectRatioLock,
    position,
    top: top || ICON_DEFAULTS.top,
    left: left || ICON_DEFAULTS.left,
    right: right || ICON_DEFAULTS.right,
    bottom: bottom || ICON_DEFAULTS.bottom,
    positionAnchorsState,
    zIndex,
    fillSpace: getLeafFillSpace(context.parentFlexDirection, ICON_DEFAULTS.fillSpace),
    flexBasis: ICON_DEFAULTS.flexBasis,
    visibility: ICON_DEFAULTS.visibility,
    tablet: { ...ICON_DEFAULTS.tablet, ...tablet },
    mobile: { ...ICON_DEFAULTS.mobile, ...mobile },
  };

  // Only include optional props if they have values
  if (borderColor) {
    props.borderColor = borderColor;
  }
  if (shadow) {
    props.fieldShadowType = shadow.shadowType;
    props.fieldShadowSettings = shadow.shadowSettings;
  }
  if (backgroundColor) {
    props.backgroundColor = backgroundColor;
    if (typeof backgroundColor !== 'string') {
      if (checkIsGradient(backgroundColor)) {
        props.bgGradient = backgroundColor as Gradient;
      } else {
        props.bgSolid = backgroundColor as RGBA;
      }
    }
  }

  return {
    type: { resolvedName: ICON_NODE_NAME },
    isCanvas: false,
    props,
    displayName: ICON_NODE_NAME,
    custom: {
      displayName: ICON_NODE_NAME,
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Map iconStyle attribute to valid icon style value
 */
const mapIconStyle = (value: string | null): 'solid' | 'regular' | undefined => {
  if (!value) return undefined;

  const normalized = value.toLowerCase().trim();
  switch (normalized) {
    case 'solid':
      return 'solid';
    case 'regular':
      return 'regular';
    default:
      return undefined;
  }
};

/**
 * Parse breakpoint-specific overrides (tablet-* or mobile-* prefixed attributes)
 */
const parseBreakpointOverrides = (
  element: Element,
  breakpoint: 'tablet' | 'mobile',
): Record<string, unknown> => {
  const overrides: Record<string, unknown> = {};

  // width
  const widthAttr = getAttribute(element, `${breakpoint}-width`);
  if (widthAttr) overrides.width = widthAttr;

  // height
  const heightAttr = getAttribute(element, `${breakpoint}-height`);
  if (heightAttr) overrides.height = heightAttr;

  // padding
  const paddingAttr = getAttribute(element, `${breakpoint}-padding`);
  if (paddingAttr) overrides.padding = parsePadding(paddingAttr, [0, 0, 0, 0]);

  // margin
  const marginAttr = getAttribute(element, `${breakpoint}-margin`);
  if (marginAttr) overrides.margin = parsePadding(marginAttr, [0, 0, 0, 0]);

  // borderRadius
  const borderRadiusAttr = getAttribute(element, `${breakpoint}-borderRadius`);
  if (borderRadiusAttr)
    overrides.borderRadius = parseBorderRadius(borderRadiusAttr, ICON_DEFAULTS.borderRadius);

  // Position attributes
  const positionAttr = getAttribute(element, `${breakpoint}-position`);
  if (positionAttr) {
    const parsedPosition = parsePosition(positionAttr);
    if (parsedPosition) overrides.position = parsedPosition;
  }

  const topAttr = getAttribute(element, `${breakpoint}-top`);
  const leftAttr = getAttribute(element, `${breakpoint}-left`);
  const rightAttr = getAttribute(element, `${breakpoint}-right`);
  const bottomAttr = getAttribute(element, `${breakpoint}-bottom`);

  const top = parsePositionOffset(topAttr);
  const left = parsePositionOffset(leftAttr);
  const right = parsePositionOffset(rightAttr);
  const bottom = parsePositionOffset(bottomAttr);

  if (top !== undefined) overrides.top = top;
  if (left !== undefined) overrides.left = left;
  if (right !== undefined) overrides.right = right;
  if (bottom !== undefined) overrides.bottom = bottom;

  // Infer positionAnchorsState if any position props are provided
  const hasPositionOverride = positionAttr || topAttr || leftAttr || rightAttr || bottomAttr;
  if (hasPositionOverride) {
    const breakpointPosition = parsePosition(positionAttr);
    overrides.positionAnchorsState = inferPositionAnchorsState(
      breakpointPosition,
      top,
      left,
      right,
      bottom,
    );
  }

  const zIndexAttr = getAttribute(element, `${breakpoint}-zIndex`);
  if (zIndexAttr) overrides.zIndex = parseInteger(zIndexAttr, 0);

  return overrides;
};

/**
 * Valid attributes for icon elements
 */
export const ICON_ATTRIBUTES = [
  'iconName',
  'iconStyle',
  'color',
  'opacity',
  'width',
  'height',
  'padding',
  'margin',
  'borderRadius',
  'borderWidth',
  'borderColor',
  'backgroundColor',
  // Shadow attributes
  ...SHADOW_ATTRIBUTES,
  // Position attributes
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'zIndex',
  // Tablet breakpoint overrides
  'tablet-width',
  'tablet-height',
  'tablet-padding',
  'tablet-margin',
  'tablet-borderRadius',
  'tablet-position',
  'tablet-top',
  'tablet-left',
  'tablet-right',
  'tablet-bottom',
  'tablet-zIndex',
  // Mobile breakpoint overrides
  'mobile-width',
  'mobile-height',
  'mobile-padding',
  'mobile-margin',
  'mobile-borderRadius',
  'mobile-position',
  'mobile-top',
  'mobile-left',
  'mobile-right',
  'mobile-bottom',
  'mobile-zIndex',
] as const;

/**
 * Check if an attribute is valid for icon elements
 */
export const isValidIconAttribute = (attr: string): boolean => {
  return (ICON_ATTRIBUTES as readonly string[]).includes(attr);
};
